import {Component, EventEmitter, forwardRef, Input, OnInit, Output} from '@angular/core';
import {AbstractControl, ControlValueAccessor, UntypedFormBuilder, UntypedFormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators} from '@angular/forms';
import {ToasterService} from '@app/shared/components/toaster/toaster.service';
import {TicketEmail, TicketEmailContentType, TicketsService, EmailTrackingCampaignName, EscalatedTicket, MessageTranslateService} from '@app/shared/services';
import {UserProfileService} from '@app/shared/services/api/user-profile.service';
import {AceValidators} from '@app/shared/util/validators/ace-validators';
import {TicketDataCaches} from "@shared/data-stores/ticket-data-caches";
import {finalize} from "rxjs/operators";


// email type should sit here to avoid circular dependency
export enum EmailType {APPROVE, FORWARD, ASSIGN}


@Component({
  selector: 'app-email-ticket',
  templateUrl: './email-ticket.component.html',
  styleUrls: ['./email-ticket.component.scss'],
  providers: [
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => EmailTicketComponent), multi: true},
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => EmailTicketComponent), multi: true},
  ],
  standalone: false
})
export class EmailTicketComponent implements OnInit, Validator, ControlValueAccessor {

  private emailContentType: TicketEmailContentType;

  @Input() ticket: EscalatedTicket;
  @Input() emailType: EmailType;
  @Input() campaignName: EmailTrackingCampaignName;

  @Output() sent: EventEmitter<TicketEmail> = new EventEmitter<TicketEmail>();
  form: UntypedFormGroup;
  ControlNames = {
    to: 'to',
    cc: 'cc',
    subject: 'subject',
    body: 'body'
  };

  equipmentLoading = false;

  private ticketId: string;
  onTouched: () => void = () => {};
  private onChange: (v: any) => void = (_) => {};

  constructor(
      private fb: UntypedFormBuilder,
      private toasterService: ToasterService,
      private translateService: MessageTranslateService,
      private ticketsService: TicketsService,
      private userProfileService: UserProfileService,
      private ticketDataCaches: TicketDataCaches
  ) {}

  ngOnInit() {
    this.createForm();
  }

  writeValue(ticket: EscalatedTicket): void {
    this.ticketId = ticket ? ticket.ticket_id : null;
    this.populateForm(ticket);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(_: AbstractControl): ValidationErrors {
    return this.form.valid ? null : {messageKey: 'Invalid email'};
  }

  private getFieldsFromForm(): TicketEmail {
    return {
      to: this.form.get(this.ControlNames.to).value.split(','),
      cc: this.form.get(this.ControlNames.cc).value ? this.form.get(this.ControlNames.cc).value.split(',') : [],
      subject: this.form.get(this.ControlNames.subject).value,
      body: this.form.get(this.ControlNames.body).value,
      ['content-type']: this.emailContentType,
    };
  }

  sendEmail() {
    this.ticketsService.sendEmail(this.getFieldsFromForm(), this.ticketId).subscribe({
      next: () => {
        this.sent.next(this.getFieldsFromForm());
      },
      error: (error) => {
        throw error;
      }
    });
  }

  private createForm() {
    this.form = this.fb.group({
      [this.ControlNames.to]: [null, [Validators.required, AceValidators.multipleEmailValidator()]],
      [this.ControlNames.cc]: [null, [AceValidators.multipleEmailValidator()]],
      [this.ControlNames.subject]: [null, [Validators.required]],
      [this.ControlNames.body]: [null, [Validators.required]],
    });
    this.form.valueChanges.subscribe((changes) => this.onChange(changes));
  }

  private populateForm(ticket: EscalatedTicket) {
    if (ticket) {
      this.equipmentLoading = true;
      this.ticketDataCaches.getEquipmentBySiteId(ticket.site_id)
        .pipe(finalize(() => this.equipmentLoading = false))
        .subscribe(equipmentById => {
          const ticketEmail = this.ticketsService.prepareEmail(
            ticket,
            [this.userProfileService.getLoggedInUser().email],
            this.campaignName,
            equipmentById)
          ;
          this.form.get([this.ControlNames.to]).setValue(ticketEmail?.to?.length ? ticketEmail.to.join(', ') : null);
          this.form.get([this.ControlNames.cc]).setValue(ticketEmail?.cc?.length ? ticketEmail.cc.join(', ') : null);
          this.form.get([this.ControlNames.subject]).setValue(ticketEmail?.subject);
          this.form.get([this.ControlNames.body]).setValue(ticketEmail?.body);
          this.emailContentType = ticketEmail?.['content-type'];
        });
    }
  }
}
